import { useState, useEffect } from "react";
import { Users, DollarSign, TrendingUp, AlertCircle } from "lucide-react";
import { reportsService } from "../../services/reports.service";
import { showToast } from "../../lib/toast";

interface SupplierOutstanding {
  supplierId: string;
  supplierName: string;
  phone: string | null;
  email: string | null;
  totalPurchases: number;
  totalPayments: number;
  outstanding: number;
  lastPaymentDate: string | null;
}

export default function SupplierOutstandingPage() {
  const [suppliers, setSuppliers] = useState<SupplierOutstanding[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalSuppliers: 0,
    totalPurchases: 0,
    totalPayments: 0,
    totalOutstanding: 0,
  });

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getSupplierOutstanding();
      setSuppliers(data);

      // Calculate summary
      const totalSuppliers = data.length;
      const totalPurchases = data.reduce((sum, s) => sum + s.totalPurchases, 0);
      const totalPayments = data.reduce((sum, s) => sum + s.totalPayments, 0);
      const totalOutstanding = data.reduce((sum, s) => sum + s.outstanding, 0);

      setSummary({
        totalSuppliers,
        totalPurchases,
        totalPayments,
        totalOutstanding,
      });
    } catch (error: any) {
      showToast.error(error.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Supplier Outstanding Report
            </h1>
            <p className="text-gray-600 mt-1">
              View outstanding payments to suppliers
            </p>
          </div>
          <button
            onClick={loadReport}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Suppliers</p>
              <p className="text-xl font-bold">{summary.totalSuppliers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Purchases</p>
              <p className="text-xl font-bold">
                ₹{summary.totalPurchases.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Paid</p>
              <p className="text-xl font-bold">
                ₹{summary.totalPayments.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-xl font-bold text-red-600">
                ₹{summary.totalOutstanding.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Supplier Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Contact
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Total Purchases
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Total Paid
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Outstanding
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Payment %
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Last Payment
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : suppliers.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No outstanding payments found
                  </td>
                </tr>
              ) : (
                suppliers.map((supplier) => {
                  const paymentPercentage =
                    supplier.totalPurchases > 0
                      ? (supplier.totalPayments / supplier.totalPurchases) * 100
                      : 0;

                  return (
                    <tr key={supplier.supplierId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {supplier.supplierName}
                          </p>
                          {supplier.email && (
                            <p className="text-xs text-gray-500">
                              {supplier.email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {supplier.phone || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        ₹{supplier.totalPurchases.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">
                        ₹{supplier.totalPayments.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-red-600">
                        ₹{supplier.outstanding.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500"
                              style={{ width: `${paymentPercentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">
                            {paymentPercentage.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {supplier.lastPaymentDate
                          ? new Date(
                              supplier.lastPaymentDate,
                            ).toLocaleDateString("en-IN", {
                              timeZone: "Asia/Kolkata",
                            })
                          : "Never"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
